/**
 * useMarketplaceSearch — TanStack Query v5 infinite-query data layer for the
 * typed-search results page (STORY-003-008).
 *
 * The SEARCH-mode sibling of `useMarketplaceBrowse` (STORY-003-005). Where the
 * browse hook calls `GET /marketplace/search?mode=browse` (empty-q list-all),
 * this hook calls `mode=search` (the default; STORY-003-002's FTS + pg_trgm
 * path) with a REQUIRED, non-empty `q`. It reuses the browse hook's local
 * `SearchResponse` / `ProductSearchItem` types verbatim (same backend envelope;
 * `apps/api/garden/features/marketplace/schemas.py`) — no codegen
 * (`packages/shared-types` is a stub).
 *
 * Query-param names are the REAL backend names (verified against
 * `public_router.py::public_search`): `mode`, `q`, `category` (uuid),
 * `market_id` (repeatable), `in_stock_only` (bool), `min_price`, `max_price`
 * (decimal), `sort`, `cursor`, `limit`. The browser query-string keys the page
 * syncs to the URL are the story's shorthand (`q`, `category`, `market`,
 * `in_stock`, `min`, `max`, `sort`) — the translation to backend names happens
 * HERE, at the API seam.
 *
 * The `enabled: q.length > 0` gate is load-bearing: the backend 422s an empty
 * `q` in search mode (`marketplace.search.empty_query`), so the query is NEVER
 * issued for an empty query (story Implementation logic + Risks & gotchas).
 *
 * No PII travels here — products, prices, markets and pavilion numbers are
 * public catalog data. The query `q` is user free-text; it rides the URL but is
 * never logged client-side.
 */
import {
  useInfiniteQuery,
  type InfiniteData,
  type UseInfiniteQueryResult,
} from "@tanstack/react-query";

import { apiClient } from "@/lib/api-client";
import {
  SEARCH_PATH,
  type ProductSearchItem,
  type SearchResponse,
} from "@/features/marketplace/useMarketplaceBrowse";

export type { ProductSearchItem, SearchResponse };

/** Max query length the backend accepts (`q` over 200 → 422; story gotcha). */
export const SEARCH_Q_MAX_LEN = 200;

/** Per-page item count (mirrors backend SEARCH_DEFAULT_LIMIT = 25). */
export const SEARCH_PAGE_LIMIT = 25;

/** staleTime per the story Implementation logic data-fetching block (30s). */
export const SEARCH_STALE_TIME_MS = 30_000;

/**
 * The filter state the results page drives the query from. `q` is the required
 * search term; the rest mirror the browse filters. These are the IN-MEMORY
 * fields; the hook maps them to the backend query-param names.
 */
export interface SearchFilters {
  /** The search query (REQUIRED for mode=search; empty disables the query). */
  q: string;
  /** Single category subtree filter (`category=<uuid>` server-side). */
  categoryId?: string | null;
  /** Repeatable market multi-select (`market_id=<uuid>&market_id=…`). */
  marketIds?: readonly string[];
  /** `in_stock_only=true` → stock_state IN ('in_stock','low'). */
  inStockOnly?: boolean;
  /** Decimal KGS lower bound (`min_price`; NO tiyin conversion). */
  minPrice?: number | null;
  /** Decimal KGS upper bound (`max_price`; NO tiyin conversion). */
  maxPrice?: number | null;
  /** `relevance` (RRF rank), `price_asc`, `price_desc`. */
  sort?: string;
}

/**
 * Stable query key. Reflects the filter snapshot so any change (including the
 * query `q` or the sort) yields a fresh cache entry and the grid is REPLACED,
 * not appended (AC #4).
 */
export function marketplaceSearchQueryKey(
  filters: SearchFilters,
): readonly unknown[] {
  return [
    "marketplace",
    "search",
    {
      q: filters.q,
      categoryId: filters.categoryId ?? null,
      marketIds: [...(filters.marketIds ?? [])].sort(),
      inStockOnly: filters.inStockOnly ?? false,
      minPrice: filters.minPrice ?? null,
      maxPrice: filters.maxPrice ?? null,
      sort: filters.sort ?? "relevance",
    },
  ];
}

/**
 * Build the `GET /marketplace/search?mode=search&q=…&…` path with the REAL
 * backend query-param names. Exported so a test can assert the exact URL the
 * hook requests. `q` is capped at `SEARCH_Q_MAX_LEN` defensively (the header
 * bar already caps the input, but the URL could carry a longer value).
 */
export function buildSearchPath(
  filters: SearchFilters,
  cursor: string | null,
): string {
  const params = new URLSearchParams();
  params.set("mode", "search");
  params.set("q", filters.q.slice(0, SEARCH_Q_MAX_LEN));
  if (filters.categoryId) {
    params.set("category", filters.categoryId);
  }
  for (const marketId of filters.marketIds ?? []) {
    params.append("market_id", marketId);
  }
  if (filters.inStockOnly) {
    params.set("in_stock_only", "true");
  }
  if (filters.minPrice !== null && filters.minPrice !== undefined) {
    params.set("min_price", String(filters.minPrice));
  }
  if (filters.maxPrice !== null && filters.maxPrice !== undefined) {
    params.set("max_price", String(filters.maxPrice));
  }
  const sort = filters.sort ?? "relevance";
  if (sort === "price_asc" || sort === "price_desc") {
    params.set("sort", sort);
  }
  params.set("limit", String(SEARCH_PAGE_LIMIT));
  if (cursor) {
    params.set("cursor", cursor);
  }
  return `${SEARCH_PATH}?${params.toString()}`;
}

export type UseMarketplaceSearchResult = UseInfiniteQueryResult<
  InfiniteData<SearchResponse, string | null>,
  Error
>;

/**
 * Fetch + cache the paginated search results. The query is GATED on a non-empty
 * `q` (`enabled: q.length > 0`) so an empty query never hits the backend (which
 * would 422 in search mode). Pages append via `next_cursor`; the query stops
 * when `next_cursor` is null.
 */
export function useMarketplaceSearch(
  filters: SearchFilters,
): UseMarketplaceSearchResult {
  return useInfiniteQuery<
    SearchResponse,
    Error,
    InfiniteData<SearchResponse, string | null>,
    readonly unknown[],
    string | null
  >({
    queryKey: marketplaceSearchQueryKey(filters),
    queryFn: ({ pageParam }): Promise<SearchResponse> =>
      apiClient.get<SearchResponse>(buildSearchPath(filters, pageParam)),
    initialPageParam: null,
    getNextPageParam: (lastPage): string | null | undefined =>
      lastPage.next_cursor ?? undefined,
    enabled: filters.q.trim().length > 0,
    staleTime: SEARCH_STALE_TIME_MS,
  });
}

/** Flatten the infinite-query pages into a single item list (render input). */
export function flattenSearchPages(
  data: InfiniteData<SearchResponse, string | null> | undefined,
): ProductSearchItem[] {
  if (!data) return [];
  return data.pages.flatMap((page) => page.items);
}
